/** @jsxImportSource @emotion/react */

import { FC, useMemo, useState } from "react";
import Icon from "../../../../components/Icons";
import { IconNames } from "../../../../components/Icons/styles/iconNames";
import { theme } from "../../../../theme";
import Typography from "../../../../components/Typography";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "../../../../hooks/useCSSRulesWithTheme";
import Column from "../../../../components/Grid/Column";
import Row from "../../../../components/Grid/Row";
import BoxContainer from "../BoxContainer";
import { IconSizes } from "../../../../components/Icons/styles/iconSizes";
import { useScreenClass } from "react-grid-system";
import Tooltip from "../../../../components/Tooltip";

export interface InsuranceTypeItem {
  label: string;
  iconName?: IconNames;
  active?: boolean;
  disabled?: boolean;
}

interface SelectInsuranceTypeProps {
  items: InsuranceTypeItem[];
}

const activeColor = theme.colors.Blue[100];
const defaultColor = theme.colors.Black[100];

const styles = {
  text: {
    textWrap: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const getItemStyles: CSSRulesResolver<{
  active?: boolean;
  disabled?: boolean;
}> = ({ active, disabled }) => ({
  card: {
    height: 80,
    padding: "0 12px",
    width: "100%",
    fontFamily: "inherit",
    fontSize: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.White[100],
    border: "solid",
    borderWidth: 2,
    borderColor: active
      ? activeColor
      : disabled
      ? theme.colors.Grey[20]
      : defaultColor,
    borderRadius: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: theme.boxShadow.default,
    transition: "border-color border-width 0.3s",
    "&:hover + !disabled": {
      borderColor: activeColor,
      borderWidth: 2,
    },
    "& span": {
      cursor: disabled ? "not-allowed" : "pointer",
    },
  },
});

const SelectInsuranceType: FC<SelectInsuranceTypeProps> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const itemStyles = (active?: boolean, disabled?: boolean) => {
    return useCSSRulesWithTheme(getItemStyles, { active, disabled });
  };

  const handleActivateItem = (item: InsuranceTypeItem, index: number) => {
    if (item.disabled) return;
    setActiveIndex(index);
  };

  const screenClass = useScreenClass();
  const avoidYPaddings = useMemo(() => {
    return (
      screenClass === "xl" || screenClass === "xxl" || screenClass === "xxxl"
    );
  }, [screenClass]);

  return (
    <BoxContainer backgroundColor={theme.colors.LightGrey[100]}>
      <Row align="center" justify="between" noMargins>
        {items.map((item, index) => (
          <Column
            xs={item.iconName ? 4 : 12}
            lg={item.iconName ? 1.5 : 3}
            key={`carousel-item-${index}`}
            useYPaddings={!avoidYPaddings}
          >
            <button
              css={itemStyles(index === activeIndex, item.disabled).card}
              onClick={() => handleActivateItem(item, index)}
            >
              {item.iconName ? (
                <Tooltip
                  id={`tooltip-insurance-${item.iconName}`}
                  text={item.label}
                >
                  <Icon
                    name={item.iconName}
                    size={IconSizes.Large}
                    disabled={item.disabled}
                  />
                </Tooltip>
              ) : (
                <Typography
                  variant="textExtraLarge"
                  weight="bold"
                  uppercase
                  css={styles.text}
                >
                  {item.label}
                </Typography>
              )}
            </button>
          </Column>
        ))}
      </Row>
    </BoxContainer>
  );
};

export default SelectInsuranceType;
